import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { getBrand } from "@/lib/brand";

const LOGO = "/angel-logo.png";

export default function Registro() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const brand = getBrand();
  const isMX = brand.id === "mx";
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [form, setForm] = useState({
    nombre: "", email: "", password: "", telefono: "",
    direccion_eu_calle: "", direccion_eu_ciudad: "", direccion_eu_estado: "", direccion_eu_zip: "",
    referred_by_code: refCode,
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (refCode) toast.success(`Te invitó alguien con código ${refCode} — ambos ganan beneficios al activar`);
  }, [refCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (!r.ok) return toast.error(r.error);
    toast.success("¡Cuenta creada! Ahora agrega beneficiarios y elige tu plan.");
    navigate("/cuenta/onboarding");
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>{`Crear cuenta — ${brand.name}`}</title>
        <meta name="description" content={`Crea tu cuenta de Membresía ${brand.name} con grúa, ambulancia, médico en línea y apoyo legal 24/7.`} />
        <meta property="og:title" content={`Crear cuenta — ${brand.name}`} />
        <meta property="og:description" content="Grúa, ambulancia, médico en línea y apoyo legal 24/7." />
        <link rel="canonical" href={brand.seo.canonical + "cuenta/registro"} />
      </Helmet>
      <div className="auth-card auth-card-wide" data-testid="registro-card">
        <Link to="/"><img src={LOGO} alt={`Membresía ${brand.name}`} className="auth-logo" /></Link>
        <h1 className="auth-title">Crea tu cuenta y protege a los tuyos</h1>
        <p className="auth-sub">{isMX ? "Tus datos de contacto en México. El registro es gratuito — solo pagas cuando actives tu membresía." : "Datos del titular (en Estados Unidos). El registro es gratuito — solo pagas cuando actives tu membresía."}</p>
        {refCode && (
          <div style={{ background: "#E8F5D9", border: "1px solid #7DC242", color: "#5BA628", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 16, fontWeight: 600 }} data-testid="ref-banner">
            🎁 Te invitó alguien con código <strong>{refCode}</strong>
          </div>
        )}
        <GoogleSignInButton label="Continuar con Google (1 click)" />
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0", color: "#9ca3af", fontSize: 12 }}>
          <span style={{ flex: 1, height: 1, background: "#e7eaf2" }} /> o regístrate con email <span style={{ flex: 1, height: 1, background: "#e7eaf2" }} />
        </div>
        <form onSubmit={onSubmit}>
          <div className="form-grid-2">
            <div className="form-group"><label>Nombre completo *</label>
              <input type="text" required value={form.nombre} onChange={set("nombre")} data-testid="reg-nombre" />
            </div>
            <div className="form-group"><label>Email *</label>
              <input type="email" required value={form.email} onChange={set("email")} data-testid="reg-email" />
            </div>
            <div className="form-group"><label>Contraseña *</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="reg-password" />
            </div>
            <div className="form-group"><label>{isMX ? "Teléfono *" : "Teléfono EU *"}</label>
              <input type="tel" required placeholder={isMX ? "+52 (XX) XXXX XXXX" : "+1 (XXX) XXX-XXXX"} value={form.telefono} onChange={set("telefono")} data-testid="reg-telefono" />
            </div>
            <div className="form-group full-width"><label>Dirección — Calle y número *</label>
              <input type="text" required value={form.direccion_eu_calle} onChange={set("direccion_eu_calle")} data-testid="reg-calle" />
            </div>
            <div className="form-group"><label>Ciudad *</label>
              <input type="text" required value={form.direccion_eu_ciudad} onChange={set("direccion_eu_ciudad")} data-testid="reg-ciudad" />
            </div>
            <div className="form-group"><label>Estado *</label>
              <input type="text" required value={form.direccion_eu_estado} onChange={set("direccion_eu_estado")} data-testid="reg-estado" />
            </div>
            <div className="form-group"><label>{isMX ? "Código Postal *" : "ZIP Code *"}</label>
              <input type="text" required value={form.direccion_eu_zip} onChange={set("direccion_eu_zip")} data-testid="reg-zip" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading} data-testid="reg-submit">
            {loading ? "Creando..." : "Crear cuenta"}
          </button>
        </form>
        <p className="auth-foot">
          ¿Ya tienes cuenta? <Link to="/cuenta/login" data-testid="go-login">Inicia sesión</Link>
        </p>
        <div className="auth-seo-block" style={{ textAlign: "left", marginTop: 28, paddingTop: 22, borderTop: "1px solid #e7eaf2" }}>
          <h2 style={{ fontSize: 16, color: "#163680", margin: "0 0 10px", fontWeight: 700, textAlign: "left" }}>¿Qué incluye tu membresía {brand.name}?</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <li style={{ fontSize: 13, color: "#4b5563", padding: "5px 0 5px 22px", position: "relative", lineHeight: 1.5, textAlign: "left" }}><span style={{ position: "absolute", left: 0, color: "#7DC242", fontWeight: 700 }}>✓</span><strong>Servicio vial 24/7</strong> — grúa, paso de corriente, cambio de llanta y combustible en cualquier estado de México.</li>
            <li style={{ fontSize: 13, color: "#4b5563", padding: "5px 0 5px 22px", position: "relative", lineHeight: 1.5, textAlign: "left" }}><span style={{ position: "absolute", left: 0, color: "#7DC242", fontWeight: 700 }}>✓</span><strong>Envío de ambulancia</strong> ante emergencias médicas con cobertura nacional.</li>
            <li style={{ fontSize: 13, color: "#4b5563", padding: "5px 0 5px 22px", position: "relative", lineHeight: 1.5, textAlign: "left" }}><span style={{ position: "absolute", left: 0, color: "#7DC242", fontWeight: 700 }}>✓</span><strong>Servicio médico telefónico</strong> con doctores certificados los 365 días del año.</li>
            <li style={{ fontSize: 13, color: "#4b5563", padding: "5px 0 5px 22px", position: "relative", lineHeight: 1.5, textAlign: "left" }}><span style={{ position: "absolute", left: 0, color: "#7DC242", fontWeight: 700 }}>✓</span><strong>Servicio legal</strong> ante accidentes, trámites o emergencias jurídicas.</li>
            <li style={{ fontSize: 13, color: "#4b5563", padding: "5px 0 5px 22px", position: "relative", lineHeight: 1.5, textAlign: "left" }}><span style={{ position: "absolute", left: 0, color: "#7DC242", fontWeight: 700 }}>✓</span><strong>Programa de referidos</strong>: regala 1 mes gratis a cada amigo que se registre y obtén 1 mes para ti.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
